import {
    AudioMessage,
    AudioMessage_Type,
    AudioMessage_ConnectInfo_ConnectStatus,
    AudioMessage_ConnectInfo_ConnectPriority,
    AudioMessage_ConnectInfo_ConnectReason,
    AudioMessage_ConnectInfo_ClientType,
    AudioMessage_Audio,
    AudioMessage_Audio_AudioEncoding,
    AudioMessage_Command,
    AudioMessage_Command_Type,
    AudioMessage_SpeakerInfo,
    AudioMessage_DevicePermissions,
    AudioMessage_DevicePermissions_BrowserPermission,
    AudioMessage_DevicePermissions_BrowserPermission_Status,
    AudioMessage_DevicePermissions_BrowserInteractionClickState,
    AudioMessage_DevicePermissions_BrowserInteractionClickState_Status,
    AudioMessage_DisplayInfo,
    AudioMessage_DisplayInfo_Orientation,
    AudioMessage_ClientLoadingStatus,
    AudioMessage_ClientPauseStatus
} from '../generated/audio_message';
import { store } from '../store/store';
import { WebSocketConnection } from "./WebSocketConnection";

export function createConnectMessage(serverUrl: string): AudioMessage {
    return AudioMessage.create({
        type: AudioMessage_Type.CLIENT_ASK_TO_CONNECT,
        infoBlock: {
            oneofKind: "connectInfo",
            connectInfo: {
                majorVersion: 4,
                minorVersion: 0,
                msgVersion: 1,
                connections: [{
                    status: AudioMessage_ConnectInfo_ConnectStatus.REQUEST,
                    conversationId: serverUrl.split('/').pop() || '',
                    lastAudioClusterSeqId: "0",
                    priority: AudioMessage_ConnectInfo_ConnectPriority.IF_AVAIL,
                    clientType: AudioMessage_ConnectInfo_ClientType.PIXELS_WEBAPP,
                    clientBuildNumber: 1,
                    clientBuildVersion: "1.0.0",
                    clientBuildName: "pixels-webapp",
                    clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    capabilities: {
                        audioClusterFinishCommands: false,
                        audioClusterTransferred: false,
                        audioClusterPlaybackHoldTime: false,
                        clusterInfoBlock: false
                    }
                }]
            }
        }
    });
}


export interface ClientInfoState {
    micOn: boolean;
    streamIsActive: boolean;
    microphonePermission: AudioMessage_DevicePermissions_BrowserPermission_Status;
    pixelStreamingInteraction: AudioMessage_DevicePermissions_BrowserInteractionClickState_Status;
    orientation?: AudioMessage_DisplayInfo_Orientation;
    windowHeight?: number;
    windowWidth?: number;
    userPauseStatus?: AudioMessage_ClientPauseStatus;
}

const clientInfoState: ClientInfoState = {
    micOn: false,
    streamIsActive: false,
    microphonePermission: AudioMessage_DevicePermissions_BrowserPermission_Status.NOT_DETERMINED,
    pixelStreamingInteraction: AudioMessage_DevicePermissions_BrowserInteractionClickState_Status.NOT_CLICKED,
    orientation: undefined,
    windowHeight: 1080, // default to max - eventually we'll only use the actual values
    windowWidth: 1920,  // default to max - eventually we'll only use the actual values
    userPauseStatus: undefined,
};

// Update one or more fields of `clientInfoState`.
// @returns `true` if at least one field changed, otherwise `false`.
function _updateClientInfoState(
    changes: Partial<ClientInfoState>,
): boolean {
    let changed = false;
    for (const key of Object.keys(changes) as (keyof ClientInfoState)[]) {
        const next = changes[key];
        if (next !== undefined && clientInfoState[key] !== next) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore – exact key type is inferred above
            clientInfoState[key] = next;
            changed = true;
        }
    }
    return changed;
}

// Sends a message to the server with the current client info state.
export function pulseClientInfoStateToServer(): void {
    const ws = WebSocketConnection.getExistingConnectedInstance();
    if (ws) {
        const m = createOutgoingAudioMessage();
        console.log("pulseClientInfoStateToServer ", m);
        ws.sendMessage(m);
    }
}

// Updates the client info state, and optionally, if anything has changed,
// sends the updated state to the server.
export function updateClientInfoState(changes: Partial<ClientInfoState>, sync: boolean = true): void {
    const stateChanged = _updateClientInfoState(changes);
    if (sync && stateChanged) {
        pulseClientInfoStateToServer();
    }
}

// A special helper to only update the display values if they have changed.
let nextDisplayId = 1;
export function updateRenderValues(orientation: AudioMessage_DisplayInfo_Orientation, width: number, height: number): void {
    if (orientation !== clientInfoState.orientation ||
        width !== clientInfoState.windowWidth ||
        height !== clientInfoState.windowHeight) {

        clientInfoState.orientation = orientation;
        clientInfoState.windowWidth = width;
        clientInfoState.windowHeight = height;

        pulseClientInfoStateToServer();
        nextDisplayId += 1;
    }
}

// Creates an outgoing audio message with the current client info state.
// Can be used stand-alone, or by other message creators to create the base message.
export function createOutgoingAudioMessage(): AudioMessage {
    const reduxEarMode = store.getState().audioSettings.desiredEarMode;

    const devicePermissions = AudioMessage_DevicePermissions.create({
        browserMicrophonePermission: AudioMessage_DevicePermissions_BrowserPermission.create({
            status: clientInfoState.microphonePermission
        }),
        browserPixelStreamingAutoPlayableInteraction: AudioMessage_DevicePermissions_BrowserInteractionClickState.create({
            status: clientInfoState.pixelStreamingInteraction
        })
    });

    const displayInfo = AudioMessage_DisplayInfo.create({
        // This is super lame. UInt64s are strings.
        displayId: BigInt(nextDisplayId).toString(),
        orientation: clientInfoState.orientation,
        renderWidth: clientInfoState.windowWidth,
        renderHeight: clientInfoState.windowHeight
    });

    return AudioMessage.create({
        type: AudioMessage_Type.CLIENT_TO_SERVER,
        infoBlock: {
            oneofKind: "clientInfo",
            clientInfo: {
                maxDeltaId: "-2",
                microphone: clientInfoState.micOn,
                microphoneMaybeTalking: clientInfoState.micOn,
                microphoneTransmitting: clientInfoState.micOn,
                earModeDesired: reduxEarMode,
                devicePermissions: devicePermissions,
                userDesiredDisplayInfo: displayInfo,
                loadingStatus: clientInfoState.streamIsActive
                    ? AudioMessage_ClientLoadingStatus.ACTIVE
                    : AudioMessage_ClientLoadingStatus.LOADING,
                userDesiredPauseStatus: clientInfoState.userPauseStatus,
            }
        }
    });
}

// Helper function to create command
function createTypedCommand(type: AudioMessage_Command_Type): AudioMessage_Command {
    return AudioMessage_Command.create({
        type
    });
}

export interface CreateAudioUploadMessageParams {
    waveform: Int16Array;
    sampleRate: number;
    clusterSeqId: number;
    clusterSampleOffset: number;
    isClusterEnd: boolean;
    isFirst: boolean;
    recordedAtMs: number;
    speaker?: AudioMessage_SpeakerInfo;
}

// Create a wave-form bearing message.
export function createAudioUploadMessage(params: CreateAudioUploadMessageParams): AudioMessage {
    const {
        waveform,
        sampleRate,
        clusterSeqId,
        clusterSampleOffset,
        isClusterEnd,
        isFirst,
        recordedAtMs,
        speaker
    } = params;

    // Get the ear mode from Redux store
    const reduxEarMode = store.getState().audioSettings.desiredEarMode;

    // Create audio block
    const audio = AudioMessage_Audio.create({
        sampleRate,
        channelCount: 1,
        bitsPerChannel: 16,
        encoding: AudioMessage_Audio_AudioEncoding.PCM,
        sampleCount: waveform.length,
        volume: 0.02,
        audioData: new Uint8Array(waveform.buffer), // Convert Int16Array to Uint8Array
        clusterEnd: isClusterEnd,
        earMode: reduxEarMode,
        recordedAtMs: String(recordedAtMs),
        speakerUid: speaker?.uid
    });

    // Force mic on in the client state.
    // NOTE: Also force streamIsActive to be true?
    updateClientInfoState({ micOn: true }, false);

    // Create base message
    const audioMessage = createOutgoingAudioMessage();

    // Add audio-specific fields
    audioMessage.audioClusterSeqId = String(clusterSeqId);
    audioMessage.audioClusterSampleOffset = String(clusterSampleOffset);
    audioMessage.audio = audio;

    // Add want-to-talk command if this is first message
    if (isFirst) {
        audioMessage.commands = [createTypedCommand(AudioMessage_Command_Type.WANT_TO_TALK)];
    }

    return audioMessage;
}

// Create a string console command message.
export function createStringConsoleMessage(text: string): AudioMessage {
    const audioMessage = createOutgoingAudioMessage();
    audioMessage.commands = [
        AudioMessage_Command.create({
            type: AudioMessage_Command_Type.STRING_CONSOLE,
            payload: {
                oneofKind: "basic",
                basic: {
                    payload: text
                }
            }
        })
    ];

    return audioMessage;
}

// Create a mic enabled/disable command message.
export function createMicEnableOrDisableMessage(userId: string, enable: boolean): AudioMessage {
    const audioMessage = createOutgoingAudioMessage();
    audioMessage.commands = [
        AudioMessage_Command.create({
            type: enable ? AudioMessage_Command_Type.ALLOW_MIC : AudioMessage_Command_Type.DISALLOW_MIC,
            payload: {
                oneofKind: "targetUser",
                targetUser: {
                    userId: userId
                }
            }
        })
    ];
    return audioMessage;
}

// Create an 'ensure talkable' (same as end-lobby) command message.
export function createEnsureTalkableSessionMessage(): AudioMessage {
    const audioMessage = createOutgoingAudioMessage();
    audioMessage.commands = [
        AudioMessage_Command.create({
            type: AudioMessage_Command_Type.SESSION_ENSURE_TALKABLE
        })
    ];
    return audioMessage;
}

// Given a message from the server, check if the server is connected.
export function isServerConnected(message: AudioMessage): { connected: boolean; error?: string } {
    if (message.type !== AudioMessage_Type.SERVER_CONNECT_INFO ||
        message.infoBlock.oneofKind !== "connectInfo") {
        return { connected: false, error: "Expected connectInfo" };
    }

    const connection = message.infoBlock.connectInfo.connections[0];
    if (!connection) {
        return { connected: false, error: "No connection info" };
    }

    if (connection.status === AudioMessage_ConnectInfo_ConnectStatus.CONNECTED) {
        return { connected: true };
    }

    // Get the status and reason as strings
    const status = connection.status !== undefined ?
        AudioMessage_ConnectInfo_ConnectStatus[connection.status] : 'UNKNOWN';
    const reason = connection.reason !== undefined ?
        AudioMessage_ConnectInfo_ConnectReason[connection.reason] : undefined;

    return {
        connected: false,
        error: reason ? `${status}: ${reason}` : status
    };
}
